import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../hooks/useApi";
import BrainCalibration from "./BrainCalibration";

// Docker control calls go to /docker/ directly (NOT through /api/ Brain proxy)
const DOCKER = axios.create({ baseURL: "/" });
DOCKER.interceptors.request.use((config) => {
  try {
    const stored = JSON.parse(localStorage.getItem("sentinel_auth"));
    if (stored?.access_token) config.headers.Authorization = `Bearer ${stored.access_token}`;
  } catch {}
  return config;
});

const s = {
  card: { background: "#0d1224", border: "1px solid #1e2a45", borderRadius: "8px", padding: "16px" },
  title: { fontSize: "14px", fontWeight: 600, color: "#60a5fa", marginBottom: "12px" },
  row: { display: "flex", gap: "8px", alignItems: "center", marginBottom: "12px", flexWrap: "wrap" },
  btn: (variant) => ({
    padding: "6px 14px", borderRadius: "5px", border: "none", cursor: "pointer",
    fontSize: "12px", fontWeight: 600,
    background: variant === "green" ? "#065f46" : variant === "red" ? "#7f1d1d" : variant === "blue" ? "#1e40af" : "#1e2a45",
    color: variant === "green" ? "#6ee7b7" : variant === "red" ? "#fca5a5" : variant === "blue" ? "#93c5fd" : "#94a3b8",
  }),
  status: (running) => ({
    padding: "4px 10px", borderRadius: "4px", fontSize: "11px", fontWeight: 600,
    background: running ? "#052e16" : "#1a0a0a",
    color: running ? "#4ade80" : "#f87171",
    border: `1px solid ${running ? "#166534" : "#7f1d1d"}`,
  }),
  logs: {
    background: "#020617", border: "1px solid #1e2a45", borderRadius: "6px",
    padding: "10px", fontFamily: "monospace", fontSize: "10px", color: "#94a3b8",
    maxHeight: "300px", overflowY: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all",
  },
  link: {
    padding: "6px 14px", borderRadius: "5px", border: "1px solid #1e40af",
    background: "#0d1224", color: "#60a5fa", fontSize: "12px", fontWeight: 600,
    textDecoration: "none", display: "inline-block",
  },
  maintenance: {
    background: "#1a0a0a", border: "2px solid #7f1d1d", borderRadius: "12px",
    padding: "40px", textAlign: "center", maxWidth: "500px", margin: "80px auto",
  },
  maintTitle: { fontSize: "24px", color: "#f87171", marginBottom: "8px" },
  maintSub: { fontSize: "14px", color: "#94a3b8" },
};

const SERVER_HOST = window.location.hostname;

export function MaintenanceBanner() {
  return (
    <div style={s.maintenance}>
      <div style={s.maintTitle}>🔧 System Under Maintenance</div>
      <div style={s.maintSub}>
        The Sentinel Brain is currently offline.<br />
        An administrator will bring it back online shortly.
      </div>
    </div>
  );
}

export default function SystemControl() {
  const [brainStatus, setBrainStatus] = useState(null);
  const [logs, setLogs] = useState("");
  const [showLogs, setShowLogs] = useState(false);
  const [loading, setLoading] = useState("");
  const [tradeStatus, setTradeStatus] = useState(null);
  const [resetting, setResetting] = useState(false);


  const fetchStatus = useCallback(async () => {
    try {
      const res = await DOCKER.get("/docker/brain/health");
      setBrainStatus(res.data);
    } catch {
      setBrainStatus({ running: false, health: "unknown" });
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 5000);
    return () => clearInterval(t);
  }, [fetchStatus]);

  const fetchTradingStatus = useCallback(async () => {
    try {
      const res = await API.get("/admin/trading_status");
      setTradeStatus(res.data);
    } catch {
      setTradeStatus(null);
    }
  }, []);

  useEffect(() => {
    fetchTradingStatus();
    const t = setInterval(fetchTradingStatus, 10000);
    return () => clearInterval(t);
  }, [fetchTradingStatus]);

  const resetTrading = async () => {
    if (!window.confirm(
      "Reset trading?\n\nRe-baselines the drawdown anchor to current equity and " +
      "clears the circuit breaker so the engine can resume. Trade history is preserved."
    )) return;
    setResetting(true);
    try {
      const res = await API.post("/admin/reset_trading", {});
      alert("Trading reset ✅\n" + JSON.stringify(res.data.summary, null, 2));
      await fetchTradingStatus();
    } catch (e) {
      alert(`Reset failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setResetting(false);
    }
  };

  const dockerAction = async (action) => {
    setLoading(action);
    try {
      await DOCKER.post(`/docker/brain/${action}`);
      await new Promise((r) => setTimeout(r, 3000));
      await fetchStatus();
    } catch (e) {
      alert(`Failed: ${e.response?.data?.error || e.message}`);
    } finally {
      setLoading("");
    }
  };

  const fetchLogs = async () => {
    try {
      const res = await DOCKER.get("/docker/brain/logs?lines=100");
      setLogs(res.data.logs || "No logs available");
      setShowLogs(true);
    } catch (e) {
      setLogs(`Error: ${e.response?.data?.error || e.message}`);
      setShowLogs(true);
    }
  };

  const running = brainStatus?.running;

  return (
    <div style={{ padding: "10px", maxWidth: "900px", margin: "0 auto" }}>
      <div style={s.card}>
        <div style={s.title}>⚙️ System Control</div>

        <div style={s.row}>
          <span style={{ fontSize: "12px", color: "#94a3b8" }}>Brain:</span>
          <span style={s.status(running)}>
            {running ? "🟢 Running" : "🔴 Stopped"}
            {brainStatus?.health && brainStatus.health !== "unknown" && ` (${brainStatus.health})`}
          </span>

          <button style={s.btn("green")} onClick={() => dockerAction("start")}
            disabled={!!loading}>{loading === "start" ? "⏳..." : "▶ Start"}</button>
          <button style={s.btn("red")} onClick={() => dockerAction("stop")}
            disabled={!!loading}>{loading === "stop" ? "⏳..." : "⏹ Stop"}</button>
          <button style={s.btn("blue")} onClick={() => dockerAction("restart")}
            disabled={!!loading}>{loading === "restart" ? "⏳..." : "🔄 Restart"}</button>
        </div>

        <div style={s.row}>
          <button style={s.btn()} onClick={fetchLogs}>
            📋 {showLogs ? "Refresh" : "Show"} Brain Logs
          </button>
        </div>

        {showLogs && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
              <span style={{ fontSize: "11px", color: "#64748b" }}>Last 100 lines</span>
              <button style={{ ...s.btn(), padding: "2px 8px", fontSize: "10px" }}
                onClick={() => setShowLogs(false)}>✕ Close</button>
            </div>
            <div style={s.logs}>{logs}</div>
          </div>
        )}
      </div>

      {/* Reset Switch — resume after a drawdown / circuit-breaker halt */}
      <div style={{ ...s.card, marginTop: "12px" }}>
        <div style={s.title}>🔁 Trading Reset</div>
        {tradeStatus ? (
          <div style={s.row}>
            <span style={{ fontSize: "12px", color: "#94a3b8" }}>
              Equity ${tradeStatus.equity} · Baseline ${tradeStatus.drawdown_baseline} · DD {tradeStatus.drawdown_pct}%
            </span>
            <span style={s.status(!tradeStatus.circuit_breaker_halt)}>
              {tradeStatus.kill_switch_halt ? "🔴 KILL-SWITCH HALT"
                : tradeStatus.circuit_breaker_halt ? "🟠 CIRCUIT-BREAKER HALT" : "🟢 Trading enabled"}
            </span>
          </div>
        ) : (
          <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "8px" }}>Status unavailable</div>
        )}
        <div style={s.row}>
          <button style={s.btn("blue")} onClick={resetTrading} disabled={resetting}>
            {resetting ? "⏳ Resetting..." : "🔁 Reset Trading (resume)"}
          </button>
          {tradeStatus?.last_reset_at && (
            <span style={{ fontSize: "11px", color: "#64748b" }}>
              Last reset: {new Date(tradeStatus.last_reset_at).toLocaleString()}
            </span>
          )}
        </div>
      </div>

      {/* Brain Calibration — Periodic Review */}
      <BrainCalibration />
    </div>
  );
}
